import { useQuery } from '@tanstack/react-query';
import { useAppStore } from '../stores/app';
import { listProblemStatements } from '../api/problems';
import { listSolutionIdeas } from '../api/solutions';
import { listCampaigns } from '../api/meta';
import { deriveStatus, type ValidationStatus } from '../lib/status';
import { EmptyState } from '../components/EmptyState';

export function Dashboard() {
  const sessionId = useAppStore((s) => s.activeSessionId);

  const { data: problems } = useQuery({
    queryKey: ['problems', sessionId],
    queryFn: () => listProblemStatements(sessionId!),
    enabled: !!sessionId,
  });

  const { data: solutions } = useQuery({
    queryKey: ['solutions', sessionId],
    queryFn: () => listSolutionIdeas(sessionId!),
    enabled: !!sessionId,
  });

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns', sessionId],
    queryFn: () => listCampaigns(sessionId!),
    enabled: !!sessionId,
  });

  if (!sessionId) {
    return (
      <EmptyState
        icon="👆"
        title="Keine Session ausgewählt"
        description="Wähle oben eine Session aus, um das Dashboard zu sehen."
      />
    );
  }

  const pList = problems ?? [];
  const sList = solutions ?? [];
  const cList = campaigns ?? [];

  // Derive counts
  const statusCounts: Record<ValidationStatus, number> = {
    DRAFT: 0,
    CREATIVE_READY: 0,
    CAMPAIGN_ACTIVE: 0,
    CAMPAIGN_PAUSED: 0,
    INSIGHTS_AVAILABLE: 0,
    VALIDATED: 0,
    REJECTED: 0,
  };

  for (const p of pList) {
    const s = deriveStatus(p);
    statusCounts[s]++;
  }

  const totalSpend = cList.reduce((sum, c) => sum + (c.spend ?? 0), 0);
  const totalClicks = cList.reduce((sum, c) => sum + (c.clicks ?? 0), 0);
  const totalImpressions = cList.reduce((sum, c) => sum + (c.impressions ?? 0), 0);
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

  const kpis = [
    { label: 'Probleme', value: pList.length, icon: '🔍' },
    { label: 'Lösungen', value: sList.length, icon: '💡' },
    { label: 'Kampagnen', value: cList.length, icon: '📢' },
    { label: 'Validiert', value: statusCounts.VALIDATED, icon: '✅' },
    { label: 'Aktive Kampagnen', value: statusCounts.CAMPAIGN_ACTIVE, icon: '🚀' },
    { label: 'Ausgaben', value: `€${(totalSpend / 100).toFixed(2)}`, icon: '💰' },
    { label: 'Klicks', value: totalClicks, icon: '👆' },
    { label: 'Ø CTR', value: `${avgCtr.toFixed(2)}%`, icon: '📈' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div
            key={k.label}
            className="bg-white border border-gray-200 rounded-xl p-4 text-center"
          >
            <span className="text-2xl">{k.icon}</span>
            <p className="text-2xl font-bold text-gray-900 mt-1">{k.value}</p>
            <p className="text-xs text-gray-500">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Status pipeline */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Validierungs-Pipeline</h2>
        <div className="flex items-center gap-2 overflow-x-auto">
          {(
            ['DRAFT', 'CREATIVE_READY', 'CAMPAIGN_ACTIVE', 'INSIGHTS_AVAILABLE', 'VALIDATED'] as ValidationStatus[]
          ).map((status, i, arr) => (
            <div key={status} className="flex items-center gap-2">
              <div className="text-center min-w-[80px]">
                <p className="text-xl font-bold text-gray-800">{statusCounts[status]}</p>
                <p className="text-[10px] text-gray-500 leading-tight">
                  {status.replace(/_/g, ' ')}
                </p>
              </div>
              {i < arr.length - 1 && (
                <span className="text-gray-300">→</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
